import React, { useMemo, useState } from 'react';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { GROWTH_CAMPAIGNS_META } from '../../growth-campaigns/config';
import { GC_PRIORITY, GC_SUGGESTIONS } from '../../growth-campaigns/mockData';
import type { GcSignalItem, GcSuggestion, GcToneKey } from '../../growth-campaigns/types';
import './growth-campaigns.css';

const GC_TONE: Record<GcToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  lav: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const scEyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

function ScRing({ score, size = 54 }: { score: number; size?: number }) {
  const r = (size - 9) / 2;
  const circ = 2 * Math.PI * r;
  const toneKey: GcToneKey = score >= 85 ? 'success' : score >= 70 ? 'gold' : 'info';
  const tone = GC_TONE[toneKey];

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-mute)"
          strokeWidth="4.5"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone.accent}
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ - (score / 100) * circ}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-[var(--font-display)] text-base font-bold tracking-[-0.03em] text-[var(--ink-0)]">
          {score}
        </span>
      </div>
    </div>
  );
}

function ScSignals({
  title,
  hint,
  icon,
  tone,
  items,
}: {
  title: string;
  hint: string;
  icon: IconKind;
  tone: GcToneKey;
  items: GcSignalItem[];
}) {
  const t = GC_TONE[tone];
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span
          className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px]"
          style={{ background: t.tint, color: t.accent }}
        >
          <Icon kind={icon} size={14} stroke={1.9} />
        </span>
        <span className="text-[13.5px] font-semibold text-[var(--ink-0)]">{title}</span>
        <span className="text-[11px] text-[var(--ink-4)]">{hint}</span>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <div
            key={item.text}
            className="flex items-start gap-2.5 rounded-[var(--r-md)] px-[13px] py-2.5"
            style={{ background: t.tint, borderLeft: `3px solid ${t.accent}` }}
          >
            <Icon kind={item.icon} size={15} stroke={1.8} className="mt-px shrink-0" style={{ color: t.accent }} />
            <span className="text-[12.5px] leading-[1.55] text-[var(--ink-1)]">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestionCard({
  suggestion,
  open,
  onToggle,
  onDismiss,
  onLaunchAutopilot,
  onLaunchReview,
}: {
  suggestion: GcSuggestion;
  open: boolean;
  onToggle: () => void;
  onDismiss: () => void;
  onLaunchAutopilot: () => void;
  onLaunchReview: () => void;
}) {
  const priority = GC_PRIORITY[suggestion.priority];
  const priorityTone = GC_TONE[priority.tone] ?? GC_TONE.gold;

  return (
    <div
      className="sc-card overflow-hidden rounded-[var(--r-xl)] bg-[var(--cream-1)]"
      style={{
        border: open
          ? '1px solid color-mix(in srgb, var(--ai-3) 40%, transparent)'
          : '1px solid var(--cream-2)',
        boxShadow: open ? 'var(--sh-2)' : 'var(--sh-1)',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="sc-head flex cursor-pointer items-center gap-[18px] px-[22px] py-[18px] transition-[background] duration-[var(--dur-fast)]"
      >
        <ScRing score={suggestion.confidence} />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              {suggestion.name}
            </span>
            <span
              className="rounded-[var(--r-pill)] px-[9px] py-[3px] text-[10px] font-bold uppercase tracking-[0.04em]"
              style={{ background: priorityTone.tint, color: priorityTone.accent }}
            >
              {priority.label}
            </span>
          </div>
          <div
            className="pr-3 text-[12.5px] leading-[1.5] text-[var(--ink-3)]"
            style={
              open
                ? undefined
                : {
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }
            }
          >
            {suggestion.reasoning}
          </div>
        </div>
        <div className="gc-head-stats flex shrink-0 gap-5">
          {[
            ['Budget', `$${suggestion.estBudget}`, 'var(--ink-1)', '/mo'],
            ['Est. bookings', suggestion.estBookings, 'var(--ai-3)', ''],
            ['Cost/patient', suggestion.estCostPerPatient, 'var(--success)', ''],
          ].map(([label, value, color, suffix]) => (
            <div key={String(label)} className="text-center">
              <div className={`${scEyebrowClass} mb-1`}>{label}</div>
              <div>
                <span
                  className="font-[var(--font-display)] text-base font-bold tracking-[-0.02em]"
                  style={{ color }}
                >
                  {value}
                </span>
                {suffix ? <span className="text-[11px] text-[var(--ink-4)]">{suffix}</span> : null}
              </div>
            </div>
          ))}
        </div>
        <Icon
          kind="down"
          size={18}
          stroke={2}
          className="shrink-0 text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </div>

      {open ? (
        <div className="elva-fade border-t border-[var(--cream-2)] p-[22px]">
          <div className="gc-signals-grid mb-[22px] grid grid-cols-2 gap-[22px]">
            <ScSignals
              title="Market signals"
              hint="— what ELVA sees in the market"
              icon="globe"
              tone="info"
              items={suggestion.marketSignals}
            />
            <ScSignals
              title="Your clinic signals"
              hint="— from your data"
              icon="home"
              tone="success"
              items={suggestion.clinicSignals}
            />
          </div>

          <div className="mb-[22px]">
            <div className={`${scEyebrowClass} mb-2`}>Ad preview</div>
            <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-[18px] py-4">
              <div className="mb-[7px] inline-flex items-center gap-1.5 text-[11px] text-[var(--ink-3)]">
                <span className="rounded border border-[var(--cream-shadow)] px-1 text-[9.5px] font-bold text-[var(--ink-2)]">
                  Ad
                </span>
                yourpractice.com
              </div>
              <div className="mb-[5px] font-[var(--font-display)] text-[17px] font-semibold leading-[1.3] tracking-[-0.01em] text-[var(--info)]">
                {suggestion.adPreview.headline}
              </div>
              <div className="text-[13px] leading-[1.6] text-[var(--ink-2)]">
                {suggestion.adPreview.desc}
              </div>
              <div className="mt-[11px] flex flex-wrap gap-1.5">
                {suggestion.keywords.map((keyword) => (
                  <span key={keyword} className="sc-kw">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="gc-metrics-grid mb-[22px] grid grid-cols-4 gap-2.5">
            {[
              ['Est. monthly budget', `$${suggestion.estBudget}`],
              ['Est. cost per click', `$${suggestion.estCPC.toFixed(2)}`],
              ['Est. new bookings', suggestion.estBookings],
              ['Est. cost per patient', suggestion.estCostPerPatient],
            ].map(([label, value]) => (
              <div
                key={String(label)}
                className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-3"
              >
                <div className={`${scEyebrowClass} mb-1.5`}>{label}</div>
                <div className="font-[var(--font-display)] text-lg font-bold tracking-[-0.02em] text-[var(--ink-0)]">
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary" iconLeft="bolt" onClick={onLaunchAutopilot}>
                Launch on autopilot
              </Button>
              <Button size="sm" variant="secondary" iconLeft="eye" onClick={onLaunchReview}>
                Launch in review mode
              </Button>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="cursor-pointer rounded-[var(--r-md)] border-0 bg-transparent px-4 py-2 font-[var(--font-body)] text-[12.5px] font-semibold text-[var(--ink-3)]"
            >
              Dismiss suggestion
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface SuggestedCampaignsViewProps {
  items?: GcSuggestion[];
  loading?: boolean;
  onDismiss?: (id: number) => void;
  onLaunchAutopilot?: (id: number) => void;
  onLaunchReview?: (id: number) => void;
}

export function SuggestedCampaignsView({
  items: itemsProp,
  loading = false,
  onDismiss,
  onLaunchAutopilot,
  onLaunchReview,
}: SuggestedCampaignsViewProps = {}) {
  const meta = GROWTH_CAMPAIGNS_META;
  const controlled = itemsProp !== undefined;
  const sourceItems = controlled ? itemsProp : GC_SUGGESTIONS;
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set([1]));
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2000);
  };

  const visible = useMemo(
    () =>
      controlled
        ? sourceItems
        : sourceItems.filter((suggestion) => !dismissed.has(suggestion.id)),
    [controlled, dismissed, sourceItems],
  );
  const highCount = visible.filter((suggestion) => suggestion.priority === 'high').length;

  const toggle = (id: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dismiss = (id: number) => {
    if (onDismiss) {
      onDismiss(id);
      return;
    }
    setDismissed((current) => new Set(current).add(id));
    showToast('Suggestion dismissed');
  };

  const launchAutopilot = (suggestion: GcSuggestion) => {
    if (onLaunchAutopilot) {
      onLaunchAutopilot(suggestion.id);
      return;
    }
    showToast(`Launching “${suggestion.name}” on autopilot`);
  };

  const launchReview = (suggestion: GcSuggestion) => {
    if (onLaunchReview) {
      onLaunchReview(suggestion.id);
      return;
    }
    showToast(`“${suggestion.name}” opened in review mode`);
  };

  return (
    <div className="growth-campaigns-root relative mx-auto w-full max-w-[1080px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-[620px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="sparkle" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <div className="gc-header-stats flex items-center gap-[18px]">
          <div className="text-right">
            <div className="font-[var(--font-display)] text-2xl font-extrabold tracking-[-0.03em] text-[var(--ai-3)]">
              {visible.length}
            </div>
            <div className={scEyebrowClass}>Suggestions</div>
          </div>
          <span className="h-[38px] w-px bg-[var(--cream-2)]" />
          <div className="text-right">
            <div className="font-[var(--font-display)] text-2xl font-extrabold tracking-[-0.03em] text-[var(--terracotta)]">
              {highCount}
            </div>
            <div className={scEyebrowClass}>High priority</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3.5">
        {visible.map((suggestion) => (
          <SuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            open={expanded.has(suggestion.id)}
            onToggle={() => toggle(suggestion.id)}
            onDismiss={() => dismiss(suggestion.id)}
            onLaunchAutopilot={() => launchAutopilot(suggestion)}
            onLaunchReview={() => launchReview(suggestion)}
          />
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="elva-fade relative overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-8 py-14 text-center shadow-[var(--sh-1)]">
          <div className="pointer-events-none absolute -top-[90px] left-1/2 h-[220px] w-[320px] -translate-x-1/2 bg-[var(--grad-spectrum)] opacity-[0.13] blur-[70px]" />
          <div className="relative">
            <span className="mx-auto mb-4 flex h-[58px] w-[58px] items-center justify-center rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-0)] text-[var(--ai-3)] shadow-[var(--sh-1)]">
              <Icon kind="sparkle" size={26} stroke={1.7} />
            </span>
            <h2 className="m-0 font-[var(--font-display)] text-[21px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              {meta.emptyTitle}
            </h2>
            <p className="mx-auto mt-2.5 max-w-[44ch] text-[13.5px] leading-[1.6] text-[var(--ink-2)]">
              {meta.emptyDescription}
            </p>
          </div>
        </div>
      ) : null}

      <div className="mt-6 flex items-center gap-2 border-t border-[var(--cream-2)] pt-4">
        <Icon kind="sparkle" size={14} stroke={1.9} className="shrink-0 text-[var(--ai-3)]" />
        <span className="text-[12.5px] leading-[1.5] text-[var(--ink-3)]">{meta.footerNote}</span>
      </div>

      {toast ? (
        <div className="elva-pop fixed bottom-6 left-1/2 z-[250] flex -translate-x-1/2 items-center gap-2 rounded-[var(--r-md)] bg-[var(--ink-0)] px-[17px] py-[11px] font-[var(--font-body)] text-[12.5px] font-medium text-[var(--cream-1)] shadow-[var(--sh-2)]">
          <Icon kind="check" size={14} stroke={2.4} className="text-[var(--ai-5)]" />
          {toast}
        </div>
      ) : null}
    </div>
  );
}
